import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '../shared';

import { ACTIONS_HISTORY_CHUNK } from './const';
import { ActionsHistoryEndpoints as Endpoints } from './types';

import type {
  GetClientActionHistoryRequest,
  GetClientActionHistoryResponse,
  GetLeadClientActionHistoryFiltersRequest,
  GetLeadClientActionHistoryFiltersResponse,
  GetLeadClientActionHistoryRequest,
  GetRecordActionHistoryRequest,
  GetRecordActionHistoryResponse,
  GetUserActionHistoryRequest,
  GetUserActionHistoryResponse,
  ModelActionHistory,
  SetLeadClientActionHistoryFiltersRequest,
  SetLeadClientActionHistoryFiltersResponse,
} from './types';

// Без таймаута зависший запрос истории чата навсегда блокирует подгрузку
// (isOlderHistoryLoading не сбрасывается) — ограничиваем ожидание явно.
const LEAD_HISTORY_TIMEOUT_MS = 30_000;

export const actionsHistoryApi = createApi({
  reducerPath: 'actionsHistoryApi',
  baseQuery: createApiBaseQuery(ACTIONS_HISTORY_CHUNK),
  tagTypes: ['ActionHistory', 'LeadClientActionHistoryFilters'],
  endpoints: ({ query, mutation }) => ({
    [Endpoints.GetUserActionHistory]: query<
      GetUserActionHistoryResponse,
      GetUserActionHistoryRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserActionHistory,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientActionHistory]: query<
      GetClientActionHistoryResponse,
      GetClientActionHistoryRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientActionHistory,
        method: Methods.GET,
        params,
      }),
      transformErrorResponse: ({ data }) => data,
      providesTags: ['ActionHistory'],
    }),
    [Endpoints.GetLeadClientActionHistory]: query<
      ModelActionHistory[],
      GetLeadClientActionHistoryRequest
    >({
      query: (params) => ({
        url: Endpoints.GetLeadClientActionHistory,
        method: Methods.GET,
        params,
        timeout: LEAD_HISTORY_TIMEOUT_MS,
      }),
      transformErrorResponse: ({ data }) => data,
      providesTags: ['ActionHistory'],
    }),
    [Endpoints.GetLeadClientActionHistoryFilters]: query<
      GetLeadClientActionHistoryFiltersResponse,
      GetLeadClientActionHistoryFiltersRequest
    >({
      query: (params) => ({
        url: Endpoints.GetLeadClientActionHistoryFilters,
        method: Methods.GET,
        params,
      }),
      providesTags: ['LeadClientActionHistoryFilters'],
    }),
    [Endpoints.SetLeadClientActionHistoryFilters]: mutation<
      SetLeadClientActionHistoryFiltersResponse,
      SetLeadClientActionHistoryFiltersRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetLeadClientActionHistoryFilters,
        method: Methods.POST,
        params,
        data,
      }),
      invalidatesTags: ['LeadClientActionHistoryFilters'],
    }),
    [Endpoints.GetRecordActionHistory]: query<
      GetRecordActionHistoryResponse,
      GetRecordActionHistoryRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordActionHistory,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useGetUserActionHistoryQuery,
  useLazyGetUserActionHistoryQuery,
  useGetClientActionHistoryQuery,
  useLazyGetClientActionHistoryQuery,
  useGetLeadClientActionHistoryQuery,
  useLazyGetLeadClientActionHistoryQuery,
  useGetRecordActionHistoryQuery,
  useLazyGetRecordActionHistoryQuery,
  useGetLeadClientActionHistoryFiltersQuery,
  useSetLeadClientActionHistoryFiltersMutation,
} = actionsHistoryApi;
